import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, Sparkles, FileText, KanbanSquare, Scissors, Boxes, Wallet,
  BarChart3, ShieldCheck, Smartphone, MessagesSquare, Menu, X, Star, Quote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { Reveal } from '@/components/marketing/Reveal';
import {
  LogoMark, DashboardMockup, QuotationMockup, KanbanMockup, ReportsMockup,
} from '@/components/marketing/Mockups';

const METRICS = [
  ['12.480+', 'Penawaran dibuat'],
  ['Rp 18,7 M', 'Nilai proyek dikelola'],
  ['320+', 'Workshop aktif'],
  ['-35%', 'Waktu administrasi'],
];

const LOGOS = ['Jaya Makmur', 'Sinta Alumindo', 'Karya Logam', 'Mega Kaca', 'Pintu Prima', 'Citra Aluminium'];

const SHOWCASES = [
  {
    kicker: 'Penawaran',
    title: 'Penawaran akurat & konsisten dengan bantuan AI',
    desc: 'Susun RAB dari ukuran ke total biaya dalam hitungan menit. Margin dan diskon terkontrol, hasil rapi siap dikirim ke pelanggan.',
    bullets: ['Template item & ukuran', 'Margin & diskon terkontrol', 'Ekspor PDF + siap WhatsApp'],
    Mockup: QuotationMockup,
    reverse: false,
  },
  {
    kicker: 'Proyek',
    title: 'Pantau progres proyek tanpa tanya-tanya',
    desc: 'Dari ukur, produksi, sampai pemasangan — semua status terlihat jelas dalam papan Kanban. Tim produksi dan lapangan selalu sinkron.',
    bullets: ['Tahap kerja fleksibel', 'PIC & tenggat per kartu', 'Catatan & foto lapangan'],
    Mockup: KanbanMockup,
    reverse: true,
  },
  {
    kicker: 'Keuangan',
    title: 'Tahu profit sebenarnya, bukan cuma omzet',
    desc: 'Lihat margin per proyek dan tren bulanan. Kelola DP, termin, dan piutang jatuh tempo agar arus kas tetap sehat.',
    bullets: ['Profit per proyek', 'Aging piutang & pengingat', 'Ringkasan bulanan + ekspor'],
    Mockup: ReportsMockup,
    reverse: false,
  },
];

const FEATURES = [
  { icon: FileText, title: 'AI Penawaran', desc: 'Dari ukuran ke total biaya — cepat, konsisten, dalam Rupiah.' },
  { icon: KanbanSquare, title: 'Kanban Proyek', desc: 'Survey sampai pasang, semua status jelas dan terpantau.' },
  { icon: Scissors, title: 'Material Optimizer', desc: 'Daftar potong batang 6m otomatis. Kurangi sisa bahan.' },
  { icon: Boxes, title: 'Inventory & Stok', desc: 'Stok aman, alert bahan menipis, reorder tepat waktu.' },
  { icon: Wallet, title: 'Pembayaran & Piutang', desc: 'DP, termin, pelunasan — rapi dan tertagih.' },
  { icon: BarChart3, title: 'Laporan Profit', desc: 'Margin per proyek, bukan sekadar angka omzet.' },
];

const STEPS = [
  { n: '01', title: 'Buat workshop', desc: 'Atur profil usaha, tim, dan template penawaran Anda.' },
  { n: '02', title: 'Jalankan proyek', desc: 'Kelola penawaran, material, dan progres produksi.' },
  { n: '03', title: 'Pantau & rapikan', desc: 'Lihat laporan, piutang, dan performa tiap proyek.' },
];

const TESTIMONIALS = [
  { name: 'Budi Santoso', role: 'Owner Workshop Aluminium · Surabaya', initials: 'BS',
    quote: 'Dulu format penawaran beda-beda. Sekarang rapi dan hitungannya konsisten — pelanggan jadi lebih percaya.', metric: 'Waktu buat penawaran turun ~30%' },
  { name: 'Rina Prameswari', role: 'Manajer Operasional · Bandung', initials: 'RP',
    quote: 'Kanban proyek bikin tim produksi dan pemasangan sinkron. Status jelas tanpa harus telepon bolak-balik.', metric: 'Revisi jadwal jauh berkurang' },
  { name: 'Hendra Wijaya', role: 'Owner Kaca & Pintu · Jakarta', initials: 'HW',
    quote: 'Piutang dan termin akhirnya kepegang. Pengingat jatuh tempo sangat membantu arus kas.', metric: 'Piutang terlambat berkurang' },
];

const TIERS = [
  { key: 'free', name: 'Gratis', price: 'Rp0', sub: '/bulan', desc: 'Untuk mencoba alur kerja dasar.', cta: 'Mulai Gratis',
    testid: 'landing-pricing-card-gratis', ctaTestid: 'landing-pricing-gratis-cta-button', featured: false,
    features: ['1 workshop & 1 pengguna', 'AI penawaran dasar (20/bln)', 'CRM & manajemen proyek', '10 penawaran / bulan', 'Pembayaran & piutang'] },
  { key: 'pro', name: 'Pro', price: 'Rp149.000', sub: '/bulan', desc: 'Untuk workshop yang ingin rapi & cepat.', cta: 'Pilih Pro', badge: 'Paling Populer',
    testid: 'landing-pricing-card-pro', ctaTestid: 'landing-pricing-pro-cta-button', featured: true,
    features: ['Penawaran & pelanggan tanpa batas', 'Tim s/d 7 (Staff & Tukang)', 'AI penuh + Asisten Insight', 'Inventory & Laporan Profit', 'Material Optimizer', 'WhatsApp follow-up & inbox'] },
  { key: 'business', name: 'Bisnis', price: 'Rp399.000', sub: '/bulan', desc: 'Untuk tim yang butuh kontrol lebih.', cta: 'Pilih Bisnis',
    testid: 'landing-pricing-card-bisnis', ctaTestid: 'landing-pricing-bisnis-cta-button', featured: false,
    features: ['Semua fitur Pro', 'Pengguna tanpa batas', 'Kuota AI tanpa batas', 'Kontrol peran lengkap', 'Dukungan prioritas'] },
];

const FAQS = [
  { q: 'Apakah AluminaOne cocok untuk workshop kecil?', a: 'Ya. Mulai dari paket Gratis untuk mencoba, lalu naik ke Pro saat alur kerja sudah stabil. Tanpa kartu kredit untuk memulai.' },
  { q: 'Apakah bisa dipakai di HP?', a: 'Bisa. AluminaOne dirancang mobile-first — owner, admin, dan tukang bisa pakai langsung dari HP di lokasi proyek.' },
  { q: 'Apakah data tiap workshop aman & terpisah?', a: 'Ya. Setiap workshop punya workspace sendiri yang terisolasi penuh. Akses pengguna mengikuti peran (Owner, Staff, Tukang).' },
  { q: 'Bagaimana cara pindah dari spreadsheet?', a: 'Mulai dari proyek baru. Untuk data lama, Anda bisa impor bertahap sesuai kebutuhan kapan saja.' },
  { q: 'Bagaimana pembayaran langganannya?', a: 'Mulai gratis 14 hari. Upgrade ke Pro/Bisnis dibayar online lewat Xendit (QRIS, Virtual Account & e-wallet) dan paket aktif otomatis.' },
];

const FOOTER_COLS = [
  { title: 'Produk', links: [['Fitur', '#fitur'], ['Harga', '#harga'], ['FAQ', '#faq']] },
  { title: 'Perusahaan', links: [['Tentang', '#'], ['Karier', '#'], ['Kontak', '#']] },
  { title: 'Legal', links: [['Kebijakan Privasi', '#'], ['Syarat Layanan', '#']] },
];

const NAV_LINKS = [['Fitur', '#fitur'], ['Produk', '#produk'], ['Harga', '#harga', 'landing-nav-pricing-link'], ['FAQ', '#faq']];

const Logo = () => (
  <div className="flex items-center gap-2">
    <LogoMark className="h-9 w-9" />
    <span className="font-display text-lg font-semibold tracking-tight">AluminaOne</span>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const goRegister = () => navigate('/login?daftar=1');
  const goLogin = () => navigate('/login');
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="landing-shell min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* ---------------- HEADER ---------------- */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/')} aria-label="AluminaOne" data-testid="marketing-header-logo-link"><Logo /></button>
          <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
            {NAV_LINKS.map(([label, href, testid]) => (
              <a key={label} href={href} data-testid={testid} className="transition-colors duration-150 hover:text-foreground">{label}</a>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <Button variant="ghost" onClick={goLogin} data-testid="landing-nav-login-button">Masuk</Button>
            <Button onClick={goRegister} data-testid="landing-nav-primary-cta-button" className="rounded-xl">Coba Gratis</Button>
          </div>
          <button className="md:hidden" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="landing-mobile-menu-button">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-border/70 bg-background px-4 py-3 md:hidden">
            <div className="flex flex-col gap-1 text-sm">
              {NAV_LINKS.map(([label, href]) => (
                <a key={label} href={href} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2 hover:bg-muted">{label}</a>
              ))}
              <div className="mt-2 flex gap-2">
                <Button variant="outline" onClick={goLogin} className="flex-1">Masuk</Button>
                <Button onClick={goRegister} className="flex-1">Coba Gratis</Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden">
        <div className="hero-gradient pointer-events-none absolute inset-x-0 top-0 h-[460px]" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 sm:pb-20 sm:pt-16 lg:grid-cols-2 lg:px-8">
          <div>
            <Badge variant="outline" className="mb-6 rounded-full border-primary/30 bg-primary/10 text-primary">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Sistem operasi workshop aluminium, kaca & pintu
            </Badge>
            <h1 data-testid="hero-headline" className="font-display text-4xl font-semibold leading-[1.07] tracking-tight sm:text-5xl lg:text-6xl">
              Kelola penawaran, proyek & stok
              <span className="text-primary"> lebih rapi dan lebih cepat.</span>
            </h1>
            <p data-testid="hero-subcopy" className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              AluminaOne membantu workshop aluminium, kaca & pintu di Indonesia membuat penawaran akurat,
              memantau progres proyek, dan mengendalikan material — tanpa spreadsheet yang berantakan.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" onClick={goRegister} data-testid="landing-hero-primary-cta-button" className="rounded-xl text-base">
                Coba Gratis 14 Hari <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo('produk')} data-testid="hero-secondary-cta-button" className="rounded-xl text-base">
                Lihat Demo Produk
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Tanpa kartu kredit • Aktif dalam 2 menit • Bahasa Indonesia & Rupiah</p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Data tiap workshop terpisah</span>
              <span className="flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Mobile-first</span>
              <span className="flex items-center gap-1.5"><MessagesSquare className="h-4 w-4 text-primary" /> WhatsApp follow-up</span>
            </div>
          </div>
          <Reveal delay={80} data-testid="hero-product-mockup">
            <DashboardMockup />
          </Reveal>
        </div>
      </section>

      {/* ---------------- LOGO BAR ---------------- */}
      <section className="border-y border-border bg-muted/30" data-testid="social-proof-logo-bar">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <p className="text-center text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Dipercaya workshop yang mengutamakan kualitas & ketepatan
          </p>
          <div className="mt-6 grid grid-cols-2 items-center gap-x-6 gap-y-4 sm:grid-cols-3 md:grid-cols-6">
            {LOGOS.map((name) => (
              <div key={name} className="flex items-center justify-center gap-1.5 opacity-60 grayscale transition-opacity duration-200 hover:opacity-100">
                <LogoMark className="h-5 w-5 rounded-md" />
                <span className="font-display text-sm font-semibold tracking-tight">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- METRIC BAND ---------------- */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8" data-testid="marketing-metric-band">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {METRICS.map(([value, label], i) => (
            <Reveal key={label} delay={i * 60} className="rounded-2xl border border-border bg-card p-5 text-left">
              <div className="font-display text-3xl font-bold tracking-tight tabular-nums sm:text-4xl">{value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- PRODUCT SHOWCASES ---------------- */}
      <section id="produk" className="border-t border-border bg-muted/20">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Produk</p>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Satu platform untuk seluruh operasional workshop</h2>
            <p className="mt-3 text-muted-foreground">Dari hitung RAB sampai tagih pelunasan — semua terhubung, tanpa salah hitung.</p>
          </div>
          <div className="mt-12 space-y-16 sm:space-y-24">
            {SHOWCASES.map(({ kicker, title, desc, bullets, Mockup, reverse }) => (
              <div key={title} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                <Reveal className={reverse ? 'lg:order-2' : ''}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{kicker}</p>
                  <h3 className="mt-2 font-display text-xl font-semibold tracking-tight sm:text-2xl">{title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-3 w-3" /></span>
                        <span className="text-foreground/90">{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={80} className={reverse ? 'lg:order-1' : ''}>
                  <Mockup />
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FEATURES BENTO ---------------- */}
      <section id="fitur" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" data-testid="marketing-features-section">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Fitur</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Semua yang dibutuhkan workshop modern</h2>
          <p className="mt-3 text-muted-foreground">Tanpa Excel berantakan, tanpa aplikasi terpisah-pisah.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 50}>
              <div data-testid="landing-feature-tile"
                className="group h-full rounded-2xl border border-border bg-card p-6 shadow-sm transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[var(--shadow-md)]">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-150 group-hover:bg-primary group-hover:text-primary-foreground">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section id="cara-kerja" className="border-y border-border bg-muted/30" data-testid="marketing-how-it-works">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Cara kerja</p>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Mulai dalam tiga langkah</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="relative h-full rounded-2xl border border-border bg-card p-6">
                  <div className="font-display text-3xl font-bold text-primary/25">{s.n}</div>
                  <h3 className="mt-3 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" data-testid="marketing-testimonials">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Testimoni</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Dipakai pelaku workshop di seluruh Indonesia</h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 70}>
              <figure data-testid={`marketing-testimonial-card-${i + 1}`} className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-0.5 text-primary">
                  {Array.from({ length: 5 }).map((_, s) => <Star key={`star-${i}-${s}`} className="h-3.5 w-3.5 fill-current" />)}
                </div>
                <Quote className="mt-3 h-5 w-5 text-primary/30" />
                <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground/90">{t.quote}</blockquote>
                <div className="mt-4 inline-flex w-fit rounded-full bg-success/10 px-2.5 py-1 text-[11px] font-semibold text-success">{t.metric}</div>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-border pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{t.initials}</span>
                  <span className="text-xs">
                    <span className="block font-semibold">{t.name}</span>
                    <span className="text-muted-foreground">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- PRICING ---------------- */}
      <section id="harga" className="border-t border-border bg-muted/20" data-testid="marketing-pricing">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Harga</p>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Harga transparan untuk UMKM</h2>
            <p className="mt-3 text-muted-foreground">Mulai gratis 14 hari. Tanpa kartu kredit. Upgrade kapan saja.</p>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {TIERS.map((t) => (
              <div key={t.key} data-testid={t.testid}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 ${t.featured ? 'border-primary/40 shadow-[var(--shadow-md)] ring-1 ring-primary/15' : 'border-border'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold">{t.name}</h3>
                  {t.badge && <Badge className="rounded-full bg-accent text-accent-foreground">{t.badge}</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-3xl font-bold tracking-tight tabular-nums">{t.price}</span>
                  <span className="pb-1 text-sm text-muted-foreground">{t.sub}</span>
                </div>
                <ul className="mt-5 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={goRegister} data-testid={t.ctaTestid}
                  variant={t.featured ? 'default' : 'outline'} className="mt-6 w-full rounded-xl">{t.cta}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section id="faq" className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" data-testid="marketing-faq">
        <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Pertanyaan yang sering ditanya</h2>
        <Accordion type="single" collapsible data-testid="landing-faq-accordion" className="mt-8">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger className="text-left text-sm font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-primary px-6 py-14 text-center sm:px-12">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-primary-foreground sm:text-3xl">Siap merapikan operasional workshop Anda?</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/85">Mulai dari Gratis. Buat penawaran pertama Anda dalam hitungan menit.</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button size="lg" onClick={goRegister} data-testid="final-cta-primary-button" className="rounded-xl bg-background text-foreground hover:bg-background/90">
              Coba Gratis Sekarang <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={goLogin} data-testid="final-cta-secondary-button" className="rounded-xl border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
              Masuk
            </Button>
          </div>
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer data-testid="marketing-footer" className="border-t border-border bg-background">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-4">
            <div className="md:col-span-1">
              <Logo />
              <p className="mt-3 max-w-xs text-sm text-muted-foreground">Sistem operasi untuk workshop aluminium, kaca & pintu di Indonesia.</p>
            </div>
            {FOOTER_COLS.map((col) => (
              <div key={col.title}>
                <div className="text-sm font-semibold">{col.title}</div>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {col.links.map(([label, href]) => (
                    <li key={label}><a href={href} className="transition-colors duration-150 hover:text-foreground">{label}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row">
            <span>© {new Date().getFullYear()} AluminaOne. Hak cipta dilindungi.</span>
            <button onClick={goLogin} className="transition-colors duration-150 hover:text-foreground">Masuk ke akun</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
